import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import toast from "react-hot-toast";
import configServ from "../../services/config";

/**
 * Leave Balance tab — same pattern as AccountDetails.jsx (single
 * employee context via companyId/employeeId props, fetch-on-mount,
 * try/catch + toast + response.status/message).
 *
 * UI: pick a Session, then add one or more rows — each row is
 * [Leave Type] [Days] [Carry Forward checkbox], with a "+ Add" button
 * to add another row so the admin can set multiple leave types one
 * at a time instead of seeing every type as a fixed card.
 *
 * Backend: POST /api/leave-balance/create
 *   { company_id, employee_id, session_id,
 *     allocations: [{ leave_type_id, allotted_leaves, is_carry_forward }, ...] }
 */
const emptyRow = () => ({ leave_type_id: "", days: "", carry_forward: false });

const LeaveBalance = ({ companyId, employeeId }) => {
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  const [sessionId, setSessionId] = useState("");
  const [rows, setRows] = useState([emptyRow()]);

  const [errors, setErrors] = useState({});

  // ---- Employee's current leave balance (shown below the form) ----
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(true);

  const fetchLeaveBalances = async () => {
    setBalanceLoading(true);
    try {
      const res = await configServ.getMyLeaveBalance({
        company_id: companyId,
        employee_id: employeeId,
      });
      if (res?.status === 200) setLeaveBalances(res.data || []);
    } catch (error) {
      console.log(error);
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && employeeId) fetchLeaveBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, employeeId]);

  // Fetch dropdown options
  const fetchOptions = async () => {
    setLoadingOptions(true);
    try {
      const [sessionsRes, typesRes] = await Promise.all([
        configServ.getSessions({ company_id: companyId }),
        configServ.getleaveTypes(),
      ]);

      if (sessionsRes?.status) {
        setSessions(sessionsRes.data);
        const active = sessionsRes.data.find((s) => s.is_active);
        if (active) setSessionId(active.id);
      }

      if (typesRes?.status) setLeaveTypes(typesRes.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    if (companyId) fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // TEMP DEBUG — remove once employee_id mismatch is confirmed fixed
  // useEffect(() => {
  //   console.log("LeaveBalance tab received -> companyId:", companyId, "employeeId:", employeeId);
  // }, [companyId, employeeId]);

  // ---- Row handlers ----
  const handleRowChange = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  // leave types already picked in other rows shouldn't be selectable again
  const availableTypesFor = (index) => {
    const pickedElsewhere = rows
      .filter((_, i) => i !== index)
      .map((r) => r.leave_type_id)
      .filter(Boolean);
    return leaveTypes.filter(
      (t) => !pickedElsewhere.includes(t.id) || t.id === rows[index].leave_type_id
    );
  };

  // ---- Validation ----
  const validate = () => {
    const newErrors = {};

    if (!sessionId) newErrors.session_id = "Session is required";

    const rowErrors = rows.map((row) => {
      const e = {};
      if (!row.leave_type_id) e.leave_type_id = "Select a leave type";
      if (row.days === "" || Number(row.days) < 0) e.days = "Enter valid days";
      return e;
    });
    if (rowErrors.some((e) => Object.keys(e).length)) newErrors.rows = rowErrors;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---- Submit ----
  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      const payload = {
        company_id: companyId,
        employee_id: employeeId,
        session_id: sessionId,
        allocations: rows.map((r) => ({
          leave_type_id: r.leave_type_id,
          allotted_leaves: Number(r.days),
          carry_forward_enabled: r.carry_forward ? 1 : 0,
        })),
      };

      const response = await configServ.createLeaveBalance(payload);

      if (response.status) {
        toast.success(response.message || "Leave balance saved successfully");
        setRows([emptyRow()]);
        fetchLeaveBalances(); // refresh balance summary below the form
      } else {
        toast.error(response.message || "Something went wrong");
      }
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" color="primary" gutterBottom fontWeight={600}>
            Create Leave Balance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set how many days of each leave type this employee gets this session.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Session"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                error={!!errors.session_id}
                helperText={errors.session_id}
                disabled={loadingOptions}
              >
                {sessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.session} ({s.start_date} – {s.end_date})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Days per Leave Type
          </Typography>

          {rows.map((row, index) => {
            const rowError = errors.rows?.[index] || {};
            return (
              <Grid
                container
                spacing={2}
                alignItems="center"
                key={index}
                sx={{ mb: 2 }}
              >
                <Grid item xs={12} sm={5}>
                  <TextField
                    select
                    fullWidth
                    label="Leave Type"
                    value={row.leave_type_id}
                    onChange={(e) =>
                      handleRowChange(index, "leave_type_id", e.target.value)
                    }
                    error={!!rowError.leave_type_id}
                    helperText={rowError.leave_type_id}
                    disabled={loadingOptions}
                  >
                    {availableTypesFor(index).map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.leave_name} ({t.leave_code})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={7} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Days"
                    value={row.days}
                    onChange={(e) => handleRowChange(index, "days", e.target.value)}
                    error={!!rowError.days}
                    helperText={rowError.days}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>

                <Grid item xs={5} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={row.carry_forward}
                        onChange={(e) =>
                          handleRowChange(index, "carry_forward", e.target.checked)
                        }
                      />
                    }
                    label="Carry Forward"
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  <IconButton
                    color="error"
                    onClick={() => removeRow(index)}
                    disabled={rows.length === 1}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}

          <Button
            startIcon={<AddIcon />}
            onClick={addRow}
            disabled={loadingOptions || rows.length >= leaveTypes.length}
            sx={{ textTransform: "none", fontWeight: 600, mt: 1 }}
          >
            Add Leave Type
          </Button>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{ borderRadius: 2, px: 4, minWidth: 170, height: 45 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Save Leave Balance"
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ---- Current leave balance for this employee ---- */}
      <Card elevation={3} sx={{ borderRadius: 3, mt: 3 }}>
        <CardContent>
          <Typography variant="h6" color="primary" gutterBottom fontWeight={600}>
            Current Leave Balance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Allotted, used and remaining days for this employee, per leave type.
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {balanceLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading leave balance...
              </Typography>
            ) : leaveBalances.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No leave balance set up yet for this employee.
              </Typography>
            ) : (
              leaveBalances.map((b) => {
                const low = Number(b.balance) <= 0;
                return (
                  <Box
                    key={b.leave_code}
                    sx={{
                      minWidth: 130,
                      backgroundColor: "#fff",
                      border: "1px solid #e0e0e0",
                      borderRadius: "10px",
                      padding: "10px 14px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {b.leave_code}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.2rem",
                        fontWeight: 700,
                        color: low ? "#c62828" : "text.primary",
                      }}
                    >
                      {b.balance}{" "}
                      <span style={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 500 }}>
                        left
                      </span>
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                      Used {b.used_leaves}
                      {Number(b.carry_forward_leaves) > 0
                        ? ` · Carried ${b.carry_forward_leaves}`
                        : ""}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LeaveBalance;